<!--
  BulkApplySetModal.vue -- Phase 4.6 full bulk-apply modal.

  Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
  Pebble: Phase 4.6 BulkApplySetModal (full UI).

  Orchestrator that wires:
    * DecorationSetPicker  (Phase 4.8) -- chosen set is selectedSetId.
    * MappingPreviewPerProduct (this PR) -- one card per product.
    * ReApprovalRollup        (this PR) -- per-client breakdown.
    * InFlightOrdersNotice    (this PR) -- M products notice.
    * ReplaceModeConfirmModal (this PR) -- persistent confirm gating
        Apply in replace mode (P4-Q7).

  Behaviour:
    1. On mount + on mode change + on selectedSetId change -- debounced
       fetchPreview against the bulkApplyPreview store.
    2. Mode toggle: merge (default) / replace. Replace surfaces the red
       pre-apply banner.
    3. Cancel: when dirty (any user override exists), open a small
       confirm sub-modal. When clean, close immediately.
    4. Apply (merge): store.apply() directly.
    5. Apply (replace): open ReplaceModeConfirmModal; on confirm call
       store.apply(). On 404 (P4.7 not shipped yet), the store flips
       applyError to a "pending" string; the modal renders the toast
       and stays open. No simulated state.

  Variant routing per ADR 0049 + P4-Q6 is APPLY-TIME logic. The
  per-product card renders a placeholder note; this modal does not
  simulate it client-side.
-->
<template>
    <div
        v-if="modelValue"
        class="bulk-apply"
        data-testid="bulk-apply-set-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-apply-title"
    >
        <div class="bulk-apply__backdrop" @click="onCancelClick"></div>
        <div class="bulk-apply__panel">
            <header class="bulk-apply__header">
                <h2 id="bulk-apply-title" class="bulk-apply__title">
                    Apply decoration set to
                    {{ selectedProductIds.length }}
                    {{
                        selectedProductIds.length === 1
                            ? 'product'
                            : 'products'
                    }}
                </h2>
            </header>

            <section class="bulk-apply__picker">
                <DecorationSetPicker
                    :model-value="selectedSetId"
                    :context-client-tag="null"
                    @update:model-value="
                        $emit('update:selectedSetId', $event)
                    "
                />
            </section>

            <section class="bulk-apply__mode" role="radiogroup" aria-label="Mode">
                <label
                    class="bulk-apply__mode-choice"
                    :class="{
                        'bulk-apply__mode-choice--active':
                            store.mode === 'merge',
                    }"
                    data-testid="bulk-apply-mode-merge"
                    @click="onModeChange('merge')"
                >
                    <input
                        type="radio"
                        name="bulk-apply-mode"
                        :checked="store.mode === 'merge'"
                    />
                    Merge
                </label>
                <label
                    class="bulk-apply__mode-choice"
                    :class="{
                        'bulk-apply__mode-choice--active':
                            store.mode === 'replace',
                    }"
                    data-testid="bulk-apply-mode-replace"
                    @click="onModeChange('replace')"
                >
                    <input
                        type="radio"
                        name="bulk-apply-mode"
                        :checked="store.mode === 'replace'"
                    />
                    Replace all
                </label>
            </section>

            <div
                v-if="store.mode === 'replace'"
                class="bulk-apply__replace-banner"
                data-testid="bulk-apply-replace-banner"
            >
                <strong>Heads up:</strong>
                {{ replaceBannerCount }} decoration{{
                    replaceBannerCount === 1 ? '' : 's'
                }}
                will be removed across
                {{ store.perProduct.length }}
                {{ store.perProduct.length === 1 ? 'product' : 'products' }}.
            </div>

            <section
                v-if="store.summary"
                class="bulk-apply__summary"
                data-testid="bulk-apply-summary"
            >
                <span class="bulk-apply__summary-pill">
                    {{ store.summary.fit_count }} ok
                </span>
                <span class="bulk-apply__summary-pill">
                    {{ store.summary.remap_count }} remap
                </span>
                <span class="bulk-apply__summary-pill">
                    {{ store.summary.drop_count }} drop
                </span>
                <span class="bulk-apply__summary-pill">
                    {{ store.summary.re_approval_count }} re-approval
                </span>
            </section>

            <ReApprovalRollup
                v-if="store.summary"
                :total-count="store.summary.re_approval_count || 0"
                :per-client="store.summary.re_approvals_per_client || []"
            />

            <InFlightOrdersNotice
                v-if="store.summary"
                :count="inFlightProductCount"
            />

            <section class="bulk-apply__products">
                <div
                    v-if="store.previewLoading"
                    class="bulk-apply__loading"
                    data-testid="bulk-apply-loading"
                >
                    Loading preview...
                </div>
                <div
                    v-else-if="store.previewError"
                    class="bulk-apply__error"
                    data-testid="bulk-apply-preview-error"
                >
                    {{ store.previewError }}
                </div>
                <MappingPreviewPerProduct
                    v-for="p in store.perProduct"
                    :key="p.campaign_product_id"
                    :product="p"
                    :mode="store.mode"
                    :remap-overrides="
                        store.remapOverrides[String(p.campaign_product_id)] ||
                        {}
                    "
                    :conflict-resolutions="
                        store.conflictResolutions[
                            String(p.campaign_product_id)
                        ] || {}
                    "
                    :available-zones="availableZones"
                    :expanded="!!expandedById[p.campaign_product_id]"
                    @toggle-expanded="onToggleExpanded(p.campaign_product_id)"
                    @remap="onRemap(p.campaign_product_id, $event)"
                    @resolve-conflict="
                        onResolveConflict(p.campaign_product_id, $event)
                    "
                />
            </section>

            <div
                v-if="store.applyError"
                class="bulk-apply__apply-error"
                data-testid="bulk-apply-apply-error"
            >
                {{ store.applyError }}
            </div>

            <footer class="bulk-apply__footer">
                <button
                    type="button"
                    class="bulk-apply__btn"
                    data-testid="bulk-apply-cancel"
                    @click="onCancelClick"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="bulk-apply__btn bulk-apply__btn--primary"
                    data-testid="bulk-apply-submit"
                    :disabled="!selectedSetId || store.applyLoading"
                    @click="onApplyClick"
                >
                    Apply to
                    {{ selectedProductIds.length }}
                    {{
                        selectedProductIds.length === 1
                            ? 'product'
                            : 'products'
                    }}
                </button>
            </footer>

            <!-- Cancel-with-edits sub-modal (P4-Q15) -->
            <div
                v-if="cancelConfirmOpen"
                class="bulk-apply__cancel-confirm"
                data-testid="bulk-apply-cancel-confirm"
                role="alertdialog"
            >
                <p>
                    You have unsaved changes (remap or conflict choices).
                    Discard and close?
                </p>
                <div class="bulk-apply__cancel-confirm-actions">
                    <button
                        type="button"
                        class="bulk-apply__btn"
                        data-testid="bulk-apply-cancel-confirm-no"
                        @click="cancelConfirmOpen = false"
                    >
                        Keep editing
                    </button>
                    <button
                        type="button"
                        class="bulk-apply__btn bulk-apply__btn--danger"
                        data-testid="bulk-apply-cancel-confirm-ok"
                        @click="confirmDiscardAndClose"
                    >
                        Discard
                    </button>
                </div>
            </div>
        </div>

        <ReplaceModeConfirmModal
            :model-value="replaceConfirmOpen"
            :decorations-to-remove="replaceBannerCount"
            :affected-products="store.perProduct.length"
            @update:model-value="replaceConfirmOpen = $event"
            @confirm="onReplaceConfirmed"
        />
    </div>
</template>

<script>
import DecorationSetPicker from '@/pages/distributor/decoration-sets/DecorationSetPicker.vue'
import MappingPreviewPerProduct from './MappingPreviewPerProduct.vue'
import ReApprovalRollup from './ReApprovalRollup.vue'
import InFlightOrdersNotice from './InFlightOrdersNotice.vue'
import ReplaceModeConfirmModal from './ReplaceModeConfirmModal.vue'
import { useBulkApplyPreviewStore } from '@/stores/bulkApplyPreview'

const PREVIEW_DEBOUNCE_MS = 200

// Reasonable default zone catalog -- the BulkApplySetModal does not yet
// have a backend endpoint for "all zones available on this garment".
// Each MappingPreviewPerProduct gets this list; the placement_zones
// table seeds the 8 canonical zones per F3.c. A follow-up can wire a
// real per-product zone fetch when SanMar catalog ingestion lands.
const FALLBACK_ZONES = [
    { slug: 'left_chest', label: 'Left chest' },
    { slug: 'right_chest', label: 'Right chest' },
    { slug: 'center_chest', label: 'Center chest' },
    { slug: 'upper_back', label: 'Upper back' },
    { slug: 'full_back', label: 'Full back' },
    { slug: 'left_sleeve', label: 'Left sleeve' },
    { slug: 'right_sleeve', label: 'Right sleeve' },
    { slug: 'cap_front', label: 'Cap front' },
]

export default {
    name: 'BulkApplySetModal',
    components: {
        DecorationSetPicker,
        MappingPreviewPerProduct,
        ReApprovalRollup,
        InFlightOrdersNotice,
        ReplaceModeConfirmModal,
    },
    props: {
        modelValue: {
            type: Boolean,
            required: true,
            default: false,
        },
        selectedSetId: {
            type: [Number, String, null],
            default: null,
        },
        selectedProductIds: {
            type: Array,
            required: true,
            default: () => [],
        },
    },
    emits: ['update:modelValue', 'update:selectedSetId', 'applied'],
    data() {
        return {
            store: useBulkApplyPreviewStore(),
            availableZones: FALLBACK_ZONES,
            expandedById: {},
            cancelConfirmOpen: false,
            replaceConfirmOpen: false,
            _previewTimer: null,
        }
    },
    computed: {
        inFlightProductCount() {
            return (this.store.perProduct || []).filter(
                (p) => (p.in_flight_orders_count || 0) > 0,
            ).length
        },
        replaceBannerCount() {
            // Rough count for the banner: in replace mode every
            // existing decoration on each selected product is on the
            // chopping block. The preview response carries one row per
            // existing-at-same-zone conflict in MERGE mode -- in
            // replace mode the backend zeroes conflicts. For the banner
            // count we conservatively use the placement_verdicts that
            // resolve to a target_zone (a stand-in until the apply
            // endpoint surfaces an exact count). This is a banner --
            // the persistent confirm modal will surface the same
            // number; both come from a single computed.
            return (this.store.perProduct || []).reduce((acc, p) => {
                return (
                    acc +
                    (p.placement_verdicts || []).filter(
                        (v) => v.target_zone_slug !== null,
                    ).length
                )
            }, 0)
        },
    },
    watch: {
        modelValue(open) {
            if (open) {
                this.store.reset()
                this.expandedById = {}
                this.cancelConfirmOpen = false
                this.replaceConfirmOpen = false
                this.scheduleFetch()
            }
        },
        selectedSetId() {
            if (this.modelValue) this.scheduleFetch()
        },
    },
    mounted() {
        if (this.modelValue) {
            this.store.reset()
            this.scheduleFetch()
        }
    },
    beforeUnmount() {
        if (this._previewTimer) {
            clearTimeout(this._previewTimer)
            this._previewTimer = null
        }
    },
    methods: {
        scheduleFetch() {
            if (this._previewTimer) {
                clearTimeout(this._previewTimer)
            }
            this._previewTimer = setTimeout(() => {
                this._previewTimer = null
                this.fetchPreview()
            }, PREVIEW_DEBOUNCE_MS)
        },
        async fetchPreview() {
            if (!this.selectedSetId) return
            if (!this.selectedProductIds.length) return
            await this.store.fetchPreview({
                setId: this.selectedSetId,
                campaignProductIds: this.selectedProductIds,
                mode: this.store.mode,
            })
        },
        onModeChange(next) {
            if (this.store.mode === next) return
            this.store.setMode(next)
            this.scheduleFetch()
        },
        onToggleExpanded(cpId) {
            this.expandedById = {
                ...this.expandedById,
                [cpId]: !this.expandedById[cpId],
            }
        },
        onRemap(cpId, { setPlacementId, zoneSlug }) {
            this.store.setRemapOverride(cpId, setPlacementId, zoneSlug)
        },
        onResolveConflict(cpId, { existingDecorationId, choice }) {
            this.store.setConflictResolution(
                cpId,
                existingDecorationId,
                choice,
            )
        },
        onCancelClick() {
            if (this.store.dirty) {
                this.cancelConfirmOpen = true
                return
            }
            this.closeModal()
        },
        confirmDiscardAndClose() {
            this.cancelConfirmOpen = false
            this.closeModal()
        },
        closeModal() {
            this.$emit('update:modelValue', false)
        },
        async onApplyClick() {
            if (this.store.mode === 'replace') {
                this.replaceConfirmOpen = true
                return
            }
            await this.doApply()
        },
        async onReplaceConfirmed() {
            this.replaceConfirmOpen = false
            await this.doApply()
        },
        async doApply() {
            await this.store.apply({
                setId: this.selectedSetId,
                campaignProductIds: this.selectedProductIds,
            })
            if (this.store.applyResult) {
                this.$emit('applied', this.store.applyResult)
                this.closeModal()
            }
            // If applyError is set (e.g., 404 pending P4.7), modal
            // stays open; the error banner is rendered above the
            // footer.
        },
    },
}
</script>

<style scoped>
.bulk-apply {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 200;
}
.bulk-apply__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
}
.bulk-apply__panel {
    position: relative;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.3);
    padding: 20px 24px;
    width: min(720px, 92vw);
    max-height: 88vh;
    overflow: auto;
    z-index: 1;
}
.bulk-apply__header {
    margin-bottom: 12px;
}
.bulk-apply__title {
    margin: 0;
    font-size: 18px;
    color: #1e293b;
}
.bulk-apply__picker {
    margin-bottom: 12px;
}
.bulk-apply__mode {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
}
.bulk-apply__mode-choice {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
}
.bulk-apply__mode-choice--active {
    border-color: #6366f1;
    background: #eef2ff;
}
.bulk-apply__replace-banner {
    margin: 0 0 12px;
    padding: 10px 12px;
    background: #fee2e2;
    border-left: 3px solid #dc2626;
    border-radius: 4px;
    font-size: 13px;
    color: #991b1b;
}
.bulk-apply__summary {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}
.bulk-apply__summary-pill {
    padding: 4px 10px;
    border-radius: 10px;
    background: #f1f5f9;
    color: #1e293b;
    font-size: 12px;
}
.bulk-apply__products {
    margin: 12px 0;
}
.bulk-apply__loading,
.bulk-apply__error {
    padding: 12px;
    font-size: 13px;
    color: #475569;
}
.bulk-apply__error {
    color: #991b1b;
}
.bulk-apply__apply-error {
    padding: 10px 12px;
    margin-bottom: 12px;
    background: #fff7ed;
    border-left: 3px solid #f59e0b;
    border-radius: 4px;
    font-size: 13px;
    color: #78350f;
}
.bulk-apply__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
}
.bulk-apply__btn {
    appearance: none;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 13px;
}
.bulk-apply__btn--primary {
    background: #6366f1;
    border-color: #6366f1;
    color: #fff;
}
.bulk-apply__btn--primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.bulk-apply__btn--danger {
    background: #dc2626;
    border-color: #dc2626;
    color: #fff;
}
.bulk-apply__cancel-confirm {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.95);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px;
    border-radius: 8px;
    text-align: center;
    z-index: 2;
}
.bulk-apply__cancel-confirm-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
}
</style>
